import { defineStore } from 'pinia';
import {
	type Category,
	type CategoryKey,
} from '../components/ReportsLibrary/types';
import { type ReportsLibraryReport } from '@/generated/graphql';
import { ReportCategoryNames } from '../components/ReportsLibrary/constants';

export const useReportsLibraryStore = defineStore('reportsLibrary', {
	state: () => {
		return {
			categories: [] as Array<Category>,
			reports: [] as Array<ReportsLibraryReport>,
			// Gates the skeleton instead of Apollo's `loading`, which can be false on a cached
			// result while the store is still empty (button + front page share the client).
			loaded: false,
			isModalOpen: false,
			selectedCategory: ReportCategoryNames.EMPTY as CategoryKey,
		};
	},

	getters: {
		hasCategories: (state) => state.categories.length > 0,
		hasReports: (state) => state.reports.length > 0,
	},

	actions: {
		/**
		 * Sets the library data and immediately re-runs the deep-link guard, so
		 * "patch then guard" is one atomic unit callers can't reorder or forget.
		 */
		setLibraryData(
			categories: Category[],
			reports: ReportsLibraryReport[]
		) {
			this.categories = categories;
			this.reports = reports;
			this.loaded = true;
			this.applyDeepLinkGuard();
		},

		/**
		 * Deep-link guard, applied after the query resolves. A
		 * `?reports_library_category=<cat>` link (see `inc/reports/reports.es6`)
		 * can select a category that has no runnable reports for the current
		 * site/user. Once the real category set is known, fall back to
		 * `all_categories` when the selected one isn't available, and never
		 * open the modal when there are no reports to show at all.
		 */
		applyDeepLinkGuard() {
			if (this.reports.length === 0) {
				this.isModalOpen = false;

				return;
			}

			if (this.selectedCategory === ReportCategoryNames.ALL_CATEGORIES) {
				return;
			}

			const isSelectedCategoryAvailable = this.categories.some(
				(category) => category.name === this.selectedCategory
			);

			if (!isSelectedCategoryAvailable) {
				this.selectedCategory = ReportCategoryNames.ALL_CATEGORIES;
			}
		},
	},
});
